import { cx } from 'class-variance-authority';
import { type ForwardRefExoticComponent, forwardRef, type RefAttributes } from 'react';
import type { Components, Options } from 'react-markdown';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { tableCellStyles } from '../table/table-styles';

type MarkdownTextProps = Options;

const defaultComponents: Components = {
  h1: ({ children }) => <h1 className="text-heading-1">{children}</h1>,
  h2: ({ children }) => <h2 className="text-heading-2">{children}</h2>,
  h3: ({ children }) => <h3 className="text-heading-3">{children}</h3>,
  h4: ({ children }) => <h4 className="text-heading-4">{children}</h4>,
  h5: ({ children }) => <h5 className="text-heading-5">{children}</h5>,
  h6: ({ children }) => <h6 className="text-heading-6">{children}</h6>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  hr: () => <hr aria-orientation="horizontal" className="w-full border-0 border-b border-tertiary my-4" />,
  blockquote: ({ children }) => (
    <blockquote className="text-gray-500 m-0 pl-12 border-l-8 border-gray-200">{children}</blockquote>
  ),
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline text-[#2D2D2D] hover:text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#2D2D2D]"
    >
      {children}
    </a>
  ),
  li: ({ children }) => <li className="mb-2">{children}</li>,
  ol: ({ children }) => <ol className="list-decimal list-outside space-y-2 pl-5">{children}</ol>,
  ul: ({ children }) => <ul className="list-disc list-outside space-y-2 pl-5">{children}</ul>,
  table: ({ children }) => (
    <div
      className="my-4 border border-tertiary rounded-md overflow-x-auto overscroll-x-none"
      style={{
        backgroundImage:
          'linear-gradient(to right, var(--background-color-primary), var(--background-color-primary)), linear-gradient(to right, var(--background-color-primary), var(--background-color-primary)), linear-gradient(to right, rgba(0, 0, 0, 0.07), rgba(0, 0, 0, 0)), linear-gradient(to left, rgba(0, 0, 0, 0.07), rgba(0, 0, 0, 0))',
        backgroundPosition: 'left center, right center, left center, right center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '24px 100%, 24px 100%, 12px 100%, 12px 100%',
        backgroundAttachment: 'local, local, scroll, scroll',
      }}
    >
      {/* Overrides the wrapper's inherited `anywhere`, which drops a cell's min-content width to
          one character — `table-auto` then collapses a narrow header column and stacks its label
          letter by letter. `break-word` keeps min-content at the longest word. */}
      <table
        className={cx(
          'border-separate min-w-full table-auto text-left [overflow-wrap:break-word]',
          tableCellStyles('default'),
        )}
      >
        {children}
      </table>
    </div>
  ),
  pre: ({ children }) => <pre className="whitespace-pre-wrap">{children}</pre>,
};

const MarkdownText: ForwardRefExoticComponent<MarkdownTextProps & RefAttributes<HTMLDivElement>> = forwardRef<
  HTMLDivElement,
  MarkdownTextProps
>(({ components, remarkPlugins, ...rest }, ref) => {
  return (
    <div
      ref={ref}
      className="text-inherit [overflow-wrap:anywhere] [&_p+p]:mt-2 [&_h1+*]:mt-2 [&_h2+*]:mt-2 [&_h3+*]:mt-2 [&_h4+*]:mt-2 [&_h5+*]:mt-2 [&_h6+*]:mt-2"
    >
      <Markdown
        {...rest}
        remarkPlugins={[remarkGfm, ...(remarkPlugins ?? [])]}
        components={{ ...defaultComponents, ...(components ?? {}) }}
      />
    </div>
  );
});

export default MarkdownText;
